import { z } from 'zod/v3';

const SerializedSignalImportDedupeFingerprintFieldsSchema = z.object({
  normalizedRoute: z.string(),
  action: z.string(),
  category: z.string(),
  queryAction: z.string().nullable(),
  mechanism: z.string().optional(),
  variant: z.string().optional(),
});

export type SerializedSignalImportDedupeFingerprintFields = z.infer<
  typeof SerializedSignalImportDedupeFingerprintFieldsSchema
>;

export const IdentityDedupCollectionSchema = z.enum([
  'signal',
  'signal_candidate',
  'signal_shadow',
  'imported_signal_draft',
]);
export type IdentityDedupCollection = z.infer<
  typeof IdentityDedupCollectionSchema
>;

export const SignalImportMechanismProviderSchema = z.enum([
  'hackerone',
  'bugcrowd',
]);
export type SignalImportMechanismProvider = z.infer<
  typeof SignalImportMechanismProviderSchema
>;

const LEGACY_MECHANISM_KEY_BY_PROVIDER: Record<
  SignalImportMechanismProvider,
  string
> = {
  [SignalImportMechanismProviderSchema.enum.hackerone]: 'weaknessName',
  [SignalImportMechanismProviderSchema.enum.bugcrowd]: 'vrtId',
} as const;

const readNonBlankString = ({
  record,
  key,
}: {
  record: Record<string, unknown>;
  key: string;
}): string | null => {
  const value = record[key];

  return typeof value === 'string' && value.trim().length > 0
    ? value.trim()
    : null;
};

export const extractStoredSignalImportMechanism = (
  metadata: Record<string, unknown> | null | undefined,
): string | null => {
  if (!metadata) {
    return null;
  }

  const provider = SignalImportMechanismProviderSchema.safeParse(
    readNonBlankString({
      record: metadata,
      key: 'provider',
    })?.toLowerCase(),
  );
  const explicitMechanism = readNonBlankString({
    record: metadata,
    key: 'vulnerabilityMechanism',
  });
  const legacyMechanismKey = provider.success
    ? LEGACY_MECHANISM_KEY_BY_PROVIDER[provider.data]
    : null;

  return (
    explicitMechanism ??
    (legacyMechanismKey
      ? readNonBlankString({ record: metadata, key: legacyMechanismKey })
      : null)
  );
};

export const isSerializedSignalImportDedupeFingerprintFields = (
  value: unknown,
): value is SerializedSignalImportDedupeFingerprintFields =>
  SerializedSignalImportDedupeFingerprintFieldsSchema.safeParse(value).success;

export const serializeSignalImportDedupeFingerprint = ({
  fields,
}: {
  fields: SerializedSignalImportDedupeFingerprintFields;
}): string =>
  JSON.stringify({
    normalizedRoute: fields.normalizedRoute,
    action: fields.action,
    category: fields.category,
    queryAction: fields.queryAction,
    ...(fields.mechanism
      ? { mechanism: fields.mechanism.trim().toLowerCase() }
      : {}),
    ...(fields.variant ? { variant: fields.variant } : {}),
  });

// Every serialized fingerprint starts with these three keys in this order, so
// the prefix matches stored fingerprints regardless of their queryAction,
// mechanism or variant suffix.
export const serializeSignalImportCoarseDedupeKeyPrefix = ({
  normalizedRoute,
  action,
  category,
}: Pick<
  SerializedSignalImportDedupeFingerprintFields,
  'normalizedRoute' | 'action' | 'category'
>): string =>
  `${JSON.stringify({ normalizedRoute, action, category }).slice(0, -1)},`;
